const REDACTED_PARAMS = new Set(['t', 'token', 'jwt', 'url', 'password', 'pin', 'cursor', 'username', 'api_key', 'apikey', 'code', 'usercode', 'devicecode', 'next']);

/** Redact credential-bearing paths, userinfo, and query parameters in a URL. */
export function redactUrlForLog(rawUrl: string): string {
    if (typeof rawUrl !== 'string') return '';
    const qIdx = rawUrl.indexOf('?');
    const hashIdx = rawUrl.indexOf('#');
    const pathEnd = [qIdx, hashIdx].filter((i) => i >= 0).reduce((a, b) => Math.min(a, b), rawUrl.length);
    let rawPath = rawUrl.slice(0, pathEnd);
    const tail = rawUrl.slice(pathEnd);
    rawPath = rawPath.replace(/(https?:\/\/)[^/@\s]+@/i, '$1REDACTED@');
    rawPath = rawPath.replace(/(\/events\/discover\/)[^/?#]+/u, '$1REDACTED');
    rawPath = rawPath.replace(/\/(live|movie|series)\/[^/]+\/[^/]+(?=\/|$)/i, '/$1/REDACTED/REDACTED');
    if (qIdx < 0) return rawPath + tail;
    const query = tail.slice(1).split('#', 1)[0]!;
    const sp = new URLSearchParams(query);
    let mutated = rawPath !== rawUrl.slice(0, pathEnd);
    for (const key of Array.from(sp.keys())) {
        if (REDACTED_PARAMS.has(key.toLowerCase())) {
            sp.set(key, 'REDACTED');
            mutated = true;
        }
    }
    if (!mutated) return rawUrl;
    const hash = tail.includes('#') ? `#${tail.split('#').slice(1).join('#')}` : '';
    return `${rawPath}?${sp.toString()}${hash}`;
}

/** Redact inline URLs in an arbitrary diagnostic string. */
export function redactTextForLog(value: string): string {
    return value.replace(/https?:\/\/[^\s'"<>]+|(?:\/api\/|\/events\/discover\/)[^\s'"<>]+/gi, (match) => {
        const trailing = match.match(/[),.;:]+$/)?.[0] ?? '';
        const core = trailing ? match.slice(0, -trailing.length) : match;
        return redactUrlForLog(core) + trailing;
    });
}

/** Clone bounded context before it enters the local ring or the wire queue. */
export function redactLogContext(value: unknown): Record<string, unknown> | undefined {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
    let budget = 0;
    const clean = (input: unknown, depth: number): unknown => {
        if (input === null || typeof input === 'boolean') return input;
        if (typeof input === 'number') return Number.isFinite(input) ? input : undefined;
        if (typeof input === 'string') return redactTextForLog(input).slice(0, 32_768);
        if (!input || typeof input !== 'object' || depth >= 5 || budget >= 256) return undefined;
        budget++;
        if (Array.isArray(input)) return input.slice(0, 24).map((item) => clean(item, depth + 1));
        const out: Record<string, unknown> = {};
        for (const key of Object.keys(input).slice(0, 48)) {
            if (/token|jwt|password|secret|authorization|cookie|username|user.?id|api.?key|user.?code|device.?code/i.test(key)) continue;
            const item = clean((input as Record<string, unknown>)[key], depth + 1);
            if (item !== undefined) Object.defineProperty(out, key.slice(0, 80), { value: item, enumerable: true, configurable: true });
        }
        return out;
    };
    try {
        return clean(value, 0) as Record<string, unknown>;
    } catch {
        return undefined;
    }
}
